import type { ImageLoaderProps } from 'next/image'

// Custom image loader for SEO-friendly URLs
export function seoImageLoader({ src, width, quality }: ImageLoaderProps): string {
  // Get the base URL
  const baseUrl =
    process.env.NEXT_PUBLIC_SERVER_URL ||
    (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000')

  // If it's already a full URL, return as is
  if (src.startsWith('http')) {
    return src
  }

  // If it's a Payload media URL
  if (src.includes('/api/media/file/')) {
    // Extract the filename from the Payload URL
    const urlParts = src.split('/')
    const filename = urlParts[urlParts.length - 1]

    // Use original filename as server handles SEO-friendly names during upload
    // Determine size suffix based on width
    let sizeSuffix = ''
    if (width <= 370) sizeSuffix = '-thumbnail'
    else if (width <= 500) sizeSuffix = '-square'
    else if (width <= 600) sizeSuffix = '-small'
    else if (width <= 900) sizeSuffix = '-medium'
    else if (width <= 1400) sizeSuffix = '-large'
    else if (width <= 1920) sizeSuffix = '-xlarge'

    // Insert size suffix before extension if needed
    let finalFilename = filename
    if (sizeSuffix) {
      const nameWithoutExt = filename.replace(/\.[^/.]+$/, '')
      const extension = filename.split('.').pop()
      finalFilename = `${nameWithoutExt}${sizeSuffix}.${extension}`
    }

    // Return URL with base URL using original filename structure
    return `${baseUrl}/resimler/${finalFilename}?w=${width}&q=${quality || 75}`
  }

  // For other relative URLs, prepend base URL
  if (src.startsWith('/')) {
    return `${baseUrl}${src}`
  }

  // Fallback for other URLs
  return src
}

// Helper function to extract original filename from URL
export function extractOriginalFilename(url: string): { filename: string; size?: string } {
  // Remove /resimler/ prefix and query params
  const cleanUrl = url.replace('/resimler/', '').split('?')[0]

  // Check for size suffixes
  const sizeMap: Record<string, string> = {
    '-thumbnail': 'thumbnail',
    '-square': 'square',
    '-small': 'small',
    '-medium': 'medium',
    '-large': 'large',
    '-xlarge': 'xlarge',
  }

  let size: string | undefined
  let baseFilename = cleanUrl

  // Check if URL contains a size suffix
  for (const [suffix, sizeKey] of Object.entries(sizeMap)) {
    if (cleanUrl.includes(suffix)) {
      size = sizeKey
      baseFilename = cleanUrl.replace(suffix, '')
      break
    }
  }

  return { filename: baseFilename, size }
}
